import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Users, Phone, Search, MapPin, Store, CheckCircle2, Ban,
  Trash2, ClipboardList, X, Calendar, LogIn as LogInIcon, Pencil, Home, FileSpreadsheet, UserPlus,
} from "lucide-react";
import { api, formatMoney, formatApiErrorDetail } from "@/lib/api";
import ImportCustomersModal from "@/components/admin/ImportCustomersModal";
import AddCustomerModal from "@/components/admin/AddCustomerModal";
import OrderStatusBadge from "@/components/OrderStatusBadge";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "blocked", label: "Blocked" },
  { key: "active", label: "Active (30d)" },
];

const within30Days = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= 30 * 24 * 60 * 60 * 1000;
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  blocked: "bg-red-100 text-red-700",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminCustomers({ initialFilter = "all", filterNonce = 0 }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [ordersFor, setOrdersFor] = useState(null); // customer object
  const [orders, setOrders] = useState(null);
  const [editing, setEditing] = useState(null); // customer being edited
  const [form, setForm] = useState({ shop_name: "", name: "", phone: "", place: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/customers").then(({ data }) => setCustomers(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setFilter(initialFilter); }, [initialFilter, filterNonce]);

  const setStatus = async (c, status) => {
    setBusyId(c.id);
    try {
      const { data } = await api.put(`/customers/${c.id}/status`, { status });
      setCustomers((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: data.status } : x)));
    } finally { setBusyId(null); }
  };

  const remove = async (c) => {
    const label = c.shop_name || c.name || c.phone;
    if (!window.confirm(`Remove customer "${label}"?\n\nThey will lose access immediately. Their past order history stays saved. This cannot be undone.`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/customers/${c.id}`);
      setCustomers((prev) => prev.filter((x) => x.id !== c.id));
    } finally { setBusyId(null); }
  };

  const viewOrders = async (c) => {
    setOrdersFor(c);
    setOrders(null);
    const { data } = await api.get(`/customers/${c.id}/orders`);
    setOrders(data);
  };

  useEffect(() => {
    if (!ordersFor) return;
    const onKey = (e) => { if (e.key === "Escape") setOrdersFor(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ordersFor]);

  const openEdit = (c) => {
    setEditing(c);
    setFormError("");
    setForm({
      shop_name: c.shop_name || "", name: c.name || "", phone: c.phone || "",
      place: c.place || "", address: c.address || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!form.shop_name.trim() || !form.name.trim() || !form.phone.trim()) {
      setFormError("Customer Name, Shop Name and Mobile Number are required.");
      return;
    }
    setSaving(true); setFormError("");
    try {
      const { data } = await api.put(`/customers/${editing.id}`, {
        shop_name: form.shop_name, name: form.name, phone: form.phone,
        place: form.place, address: form.address,
      });
      setCustomers((prev) => prev.map((x) => (x.id === editing.id
        ? { ...x, shop_name: data.shop_name, name: data.name, phone: data.phone, place: data.place, address: data.address }
        : x)));
      setEditing(null);
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => { if (e.key === "Escape") setEditing(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing]);

  const q = query.trim().toLowerCase();
  const matchesFilter = (c) => {
    const st = c.status || "pending";
    if (filter === "all") return true;
    if (filter === "active") return st === "approved" && within30Days(c.last_login);
    return st === filter;
  };
  const filtered = customers.filter((c) => {
    if (!matchesFilter(c)) return false;
    if (!q) return true;
    return [c.shop_name, c.name, c.phone, c.place].some((v) => (v || "").toLowerCase().includes(q));
  });

  const counts = customers.reduce((a, c) => {
    const st = c.status || "pending";
    a[st] = (a[st] || 0) + 1;
    if (st === "approved" && within30Days(c.last_login)) a.active = (a.active || 0) + 1;
    return a;
  }, {});

  return (
    <div data-testid="admin-customers">
      {/* Filters + search */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const c = f.key === "all" ? customers.length : (counts[f.key] || 0);
            return (
              <button
                key={f.key}
                data-testid={`customer-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                }`}
              >
                {f.label} <span className={active ? "text-blue-100" : "text-slate-400"}>({c})</span>
              </button>
            );
          })}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
          <button
            data-testid="add-customer-button"
            onClick={() => setAddOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-700 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
          >
            <UserPlus className="h-4 w-4" /> Add Customer
          </button>
          <button
            data-testid="import-customers-button"
            onClick={() => setImportOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <FileSpreadsheet className="h-4 w-4" /> Import Customers
          </button>
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="customer-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search shop, name, mobile, place..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>
      </div>

      {importOpen && (
        <ImportCustomersModal onClose={() => setImportOpen(false)} onImported={load} />
      )}
      {addOpen && <AddCustomerModal onClose={() => setAddOpen(false)} onSaved={load} />}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center text-slate-400">
          <Users className="h-10 w-10" />
          <p className="text-sm">No customers found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const status = c.status || "pending";
            const busy = busyId === c.id;
            return (
              <div key={c.id} data-testid={`admin-customer-${c.id}`} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {c.shop_name ? (
                        <p data-testid={`customer-shopname-${c.id}`} className="truncate text-base font-bold text-slate-800">
                          {c.shop_name}
                        </p>
                      ) : (
                        <button
                          data-testid={`add-details-customer-${c.id}`}
                          onClick={() => openEdit(c)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-blue-300 px-2 py-0.5 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Add shop details
                        </button>
                      )}
                      <span data-testid={`customer-status-${c.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${STATUS_STYLES[status]}`}>
                        {status}
                      </span>
                    </div>
                    <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-600">
                      <Users className="h-3.5 w-3.5 text-blue-400" /> {c.name || "—"}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-blue-400" />{c.phone}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-blue-400" />{c.place || "—"}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-slate-400" />Reg: {fmtDate(c.created_at)}</span>
                      <span className="inline-flex items-center gap-1"><LogInIcon className="h-3.5 w-3.5 text-slate-400" />Last login: {fmtDate(c.last_login)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 text-xs text-slate-500">
                      <span data-testid={`customer-orders-${c.id}`} className="font-semibold text-blue-700">{c.order_count} order(s)</span>
                      <span>Last order: {fmtDate(c.last_order_date)}</span>
                    </div>
                    {c.address && <p className="mt-1 text-xs text-slate-400">{c.address}</p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-50 pt-3">
                  {status !== "approved" && (
                    <button
                      data-testid={`approve-customer-${c.id}`}
                      disabled={busy}
                      onClick={() => setStatus(c, "approved")}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </button>
                  )}
                  {status !== "blocked" ? (
                    <button
                      data-testid={`block-customer-${c.id}`}
                      disabled={busy}
                      onClick={() => setStatus(c, "blocked")}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-60"
                    >
                      <Ban className="h-3.5 w-3.5" /> Block / Disable
                    </button>
                  ) : (
                    <button
                      data-testid={`unblock-customer-${c.id}`}
                      disabled={busy}
                      onClick={() => setStatus(c, "approved")}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Re-Activate
                    </button>
                  )}
                  <button
                    data-testid={`edit-customer-${c.id}`}
                    onClick={() => openEdit(c)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    data-testid={`view-orders-customer-${c.id}`}
                    onClick={() => viewOrders(c)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                  >
                    <ClipboardList className="h-3.5 w-3.5" /> View Orders
                  </button>
                  <button
                    data-testid={`remove-customer-${c.id}`}
                    disabled={busy}
                    onClick={() => remove(c)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-60"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                  {busy && <Loader2 className="h-4 w-4 animate-spin self-center text-slate-400" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View orders modal */}
      {ordersFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOrdersFor(null)} />
          <div data-testid="customer-orders-modal" className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-5">
              <div>
                <h3 className="text-lg font-bold text-slate-800">{ordersFor.shop_name || ordersFor.name || "Customer"}</h3>
                <p className="text-xs text-slate-500">{ordersFor.name} · {ordersFor.phone}{ordersFor.place ? ` · ${ordersFor.place}` : ""}</p>
              </div>
              <button onClick={() => setOrdersFor(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-5">
              {orders === null ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
              ) : orders.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">No orders yet.</p>
              ) : (
                <div className="space-y-3">
                  {orders.map((o) => (
                    <div key={o.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-slate-500">
                          <span className="font-semibold text-slate-700">#{o.id.slice(-6).toUpperCase()}</span> · {new Date(o.created_at).toLocaleString("en-IN")}
                        </div>
                        <OrderStatusBadge status={o.status} />
                      </div>
                      <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                        {o.items.map((it, i) => (
                          <div key={i} className="flex justify-between">
                            <span>{it.is_free ? "🎁 " : ""}{it.name}{it.unit ? ` · ${it.unit}` : ""} × {it.qty}</span>
                            <span>{it.is_free ? "₹0" : formatMoney(it.line_total)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex justify-between border-t border-slate-50 pt-2 text-sm font-bold text-slate-800">
                        <span>Total</span><span className="text-blue-700">{formatMoney(o.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit customer modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setEditing(null)} />
          <form onSubmit={saveEdit} data-testid="edit-customer-modal" className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Edit Customer</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Shop Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Store className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" />
                  <input data-testid="edit-shopname-input" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} placeholder="Shop / business name" className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" autoFocus />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Customer Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Users className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" />
                  <input data-testid="edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Customer name" className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile Number <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" />
                  <input data-testid="edit-phone-input" type="tel" inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile number" className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Place / Area</label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" />
                  <input data-testid="edit-place-input" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="Village / Town / Area" className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Full Address</label>
                <div className="relative">
                  <Home className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-blue-400" />
                  <textarea data-testid="edit-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full delivery address" className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </div>
              </div>
            </div>
            {formError && <p data-testid="edit-customer-error" className="mt-3 text-sm font-medium text-red-600">{formError}</p>}
            <div className="mt-5 flex gap-2">
              <button type="button" onClick={() => setEditing(null)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="save-customer-button" type="submit" disabled={saving} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
